import { useDroppable } from '@dnd-kit/core';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { ScheduleJobCard } from './ScheduleJobCard';
import { dropId, jobWindow, type ScheduleJob, type ScheduleLane } from './scheduleModel';

const NAME_PX = 168;
const DAY_MIN_PX = 150;

interface WeekGridProps {
  lanes: ScheduleLane[];
  days: string[];
  /** laneKey → day → jobs */
  cells: Map<string, Map<string, ScheduleJob[]>>;
  today: string;
  timezone: string;
  dragging: boolean;
  selectedJobId: string | null;
  onOpenJob: (id: string) => void;
  onOpenDay: (day: string) => void;
}

export function WeekGrid({
  lanes, days, cells, today, timezone, dragging, selectedJobId, onOpenJob, onOpenDay,
}: WeekGridProps) {
  return (
    <div className="overflow-x-auto rounded-lg border bg-card">
      <div style={{ minWidth: NAME_PX + days.length * DAY_MIN_PX }}>
        <div className="flex border-b text-xs">
          <div className="sticky left-0 z-10 shrink-0 border-r bg-card px-3 py-2 font-medium text-muted-foreground" style={{ width: NAME_PX }}>
            Technician
          </div>
          {days.map((day) => {
            const count = lanes.reduce((n, lane) => n + (cells.get(lane.key)?.get(day)?.length ?? 0), 0);
            return (
              <button
                key={day}
                type="button"
                onClick={() => onOpenDay(day)}
                className={cn(
                  'flex-1 border-r px-2 py-2 text-left last:border-r-0 hover:bg-accent',
                  day === today && 'bg-primary/10',
                )}
                style={{ minWidth: DAY_MIN_PX }}
                title="Open this day"
              >
                <span className={cn('font-semibold', day === today && 'text-primary')}>
                  {format(parseISO(day), 'EEE d')}
                </span>
                <span className="ml-1.5 text-muted-foreground">{count} job{count === 1 ? '' : 's'}</span>
              </button>
            );
          })}
        </div>

        {lanes.map((lane) => {
          const total = days.reduce((n, day) => n + (cells.get(lane.key)?.get(day)?.length ?? 0), 0);
          const isUnassigned = lane.techId === null;
          return (
            <div key={lane.key} className={cn('flex border-b last:border-b-0', isUnassigned && 'bg-muted/40')}>
              <div
                className={cn('sticky left-0 z-20 flex shrink-0 items-start gap-2 border-r px-3 py-2', isUnassigned ? 'bg-muted' : 'bg-card')}
                style={{ width: NAME_PX }}
              >
                <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: lane.colour }} />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium">{lane.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {total} this week{lane.inactive ? ' · inactive' : ''}
                  </span>
                </span>
              </div>
              {days.map((day) => (
                <WeekCell
                  key={day}
                  lane={lane}
                  day={day}
                  jobs={cells.get(lane.key)?.get(day) ?? []}
                  isToday={day === today}
                  timezone={timezone}
                  dragging={dragging}
                  selectedJobId={selectedJobId}
                  onOpenJob={onOpenJob}
                />
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface WeekCellProps {
  lane: ScheduleLane;
  day: string;
  jobs: ScheduleJob[];
  isToday: boolean;
  timezone: string;
  dragging: boolean;
  selectedJobId: string | null;
  onOpenJob: (id: string) => void;
}

function WeekCell({ lane, day, jobs, isToday, timezone, dragging, selectedJobId, onOpenJob }: WeekCellProps) {
  const { setNodeRef, isOver } = useDroppable({ id: dropId({ view: 'week', laneKey: lane.key, day }) });
  // Timed jobs by start, anytime jobs after them.
  const sorted = [...jobs].sort((a, b) => (jobWindow(a)?.start ?? 1e4) - (jobWindow(b)?.start ?? 1e4));

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-[72px] flex-1 flex-col gap-1 border-r p-1 transition-colors last:border-r-0',
        isToday && 'bg-primary/5',
        dragging && 'bg-muted/20',
        isOver && 'bg-primary/10',
      )}
      style={{ minWidth: DAY_MIN_PX }}
    >
      {sorted.map((job) => (
        <ScheduleJobCard
          key={job.id}
          job={job}
          colour={lane.colour}
          timezone={timezone}
          compact
          selected={job.id === selectedJobId}
          onOpen={onOpenJob}
        />
      ))}
    </div>
  );
}
